"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import Link from "next/link";

import { DEMO_BOOKING_URL } from "@/app/(marketing)/_components/nav-links";
import { Container } from "@/app/(marketing)/_components/ui/container";

const steps = [
  {
    number: "01",
    title: "Describe your ideal user",
    description:
      "Tell us who you want to talk to in plain English. Our AI translates your requirements into targeting criteria.",
  },
  {
    number: "02",
    title: "AI screens & qualifies",
    description:
      "AI agents call potential experts, run screening questions, and verify they match your criteria.",
  },
  {
    number: "03",
    title: "Talk to real users",
    description:
      "Qualified experts are scheduled for your study. Get transcripts, insights, and recordings automatically.",
  },
];

export default function UserResearchHowItWorks() {
  return (
    <section className="py-20">
      <Container>
        {/* Main card */}
        <div className="border-marketing-border bg-marketing-cream-light rounded-[20px] border px-6 py-12 md:px-12 lg:px-20 lg:py-16">
          {/* Header */}
          <div className="mb-16 flex flex-col items-center gap-8">
            <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
              <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
                How it works
              </p>
            </div>
            <h2 className="text-marketing-text max-w-[600px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
              From idea to insight
            </h2>
            <p className="text-marketing-text max-w-[500px] text-center text-lg font-medium">
              Launch your user research study and start talking to real users
              today.
            </p>
          </div>

          {/* Steps */}
          <div className="mx-auto max-w-3xl">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, transform: "translateY(20px)" }}
                whileInView={{ opacity: 1, transform: "translateY(0px)" }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="border-marketing-border flex gap-6 border-b py-8 last:border-b-0"
              >
                <span className="text-marketing-blue font-mono text-lg font-medium">
                  {step.number}
                </span>
                <div>
                  <h3 className="text-marketing-text mb-2 text-xl font-medium">
                    {step.title}
                  </h3>
                  <p className="text-marketing-text-muted text-base leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 flex justify-center">
            <Link
              href={DEMO_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-marketing-blue inline-flex items-center gap-2 text-base font-medium"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span>Book a call</span>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
}
